import type { AccountsUsageInfo } from '@customerio/hydra-ui-api';
import { hydraUiApi, useHydraUIAPIQuery } from '@/services/api';
import { useAuth } from '@/services/auth';
import { isFlexPlan } from '@/services/auth/plan-slugs';
import { hasUnbilledEnvironment, toBillingUsage } from '@/services/billing/transforms';
import { dollarsToCents } from '@/utils/currency';

export type FlexUsage = {
  emailSpendCents: number;
  emailSends: number;
  activeProfiles: number;
  storedProfiles: number;
};

// `GET /usage` serves only the Builder rate. Source: services `domains/wallet/meter.go`.
export const FLEX_EMAIL_DOLLARS_PER_SEND = 0.0015;

export type FlexWallet =
  | { status: 'loading' }
  | { status: 'unavailable' }
  | {
      status: 'ready';
      balanceCents: number;
      usedCents: number;
      hasFunded: boolean;
      usage: FlexUsage;
    };

export const FLEX_WALLET_QUERY_KEY = ['getAccountUsage'];

/** `cache.invalidate` type an embedded app sends after it spends from the wallet, e.g. Design Studio's inbox previews. */
export const FLEX_WALLET_CACHE_TYPE = 'flex_wallet';

export function useFlexWallet(): FlexWallet {
  const { account } = useAuth();
  const isFlex = isFlexPlan(account);

  const { data, isLoading, isError } = useHydraUIAPIQuery({
    queryKey: [...FLEX_WALLET_QUERY_KEY, { params: { account_id: account.id } }],
    queryFn: ({ signal }) =>
      hydraUiApi.accounts.getUsage({
        params: { path: { account_id: String(account.id) } },
        signal,
      }),
    queryConfig: { enabled: isFlex },
  });

  if (!isFlex || (isError && data == null)) return { status: 'unavailable' };
  if (isLoading || data == null) return { status: 'loading' };

  const accountUsage = data.account_usage;
  const wallet = accountUsage.wallet;

  return {
    status: 'ready',
    balanceCents: dollarsToCents(wallet?.balance ?? 0),
    usedCents: dollarsToCents(wallet?.total_spent ?? 0),
    hasFunded: wallet?.has_funded ?? false,
    usage: toFlexUsage(accountUsage),
  };
}

function toFlexUsage(accountUsage: AccountsUsageInfo): FlexUsage {
  const usage = toBillingUsage(accountUsage);
  const storedProfiles = hasUnbilledEnvironment(usage.environments)
    ? usage.environments.reduce((sum, environment) => sum + environment.customersTotal, 0)
    : usage.customersTotal;

  return {
    emailSends: usage.billableMessagesSent,
    emailSpendCents: Math.round(usage.billableMessagesSent * FLEX_EMAIL_DOLLARS_PER_SEND * 100),
    activeProfiles: usage.monthlyActiveUsers,
    storedProfiles,
  };
}
